import { forwardRef } from 'react';
import { useAccessibilityPreferences } from '../../hooks/useAccessibility';
import { useRTL } from '../../hooks/useRTL';
import type { ButtonProps } from '../../types/common';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  onClick,
  disabled = false,
  variant = 'primary',
  size = 'md',
  type = 'button',
  className = '',
  ariaLabel,
  ...props
}, ref) => {
  const { reducedMotion } = useAccessibilityPreferences();
  const { } = useRTL();

  const baseClasses = [
    'inline-flex items-center justify-center font-medium rounded-md',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'transition-colors duration-200',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'flex-row-reverse-rtl',
    reducedMotion ? 'motion-reduce' : '',
  ].filter(Boolean).join(' ');

  const variantClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary', 
    danger: 'btn-danger',
    success: 'btn-success',
  };

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const combinedClasses = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={combinedClasses}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      {...props}
    >
      {children}
    </button>
  );
});

Button.displayName = 'Button';